import * as React from "react";
import * as z from "zod";

import { AddIcon, ArrowIcon, DeleteIcon, EditIcon } from "components/icons";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useMemo, useState } from "react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { DropdownAutoCompleteSelect } from "components/form-control";
import { IOffStreetParking } from "types";
import { IconButton } from "components";
import { Input } from "@/components/ui/input";
import { InputNumber } from "@/components/form-control/input-number";
import { Label } from "components/ui/label";
import {
  OffStreetParkingModalState,
  PARKING_LAYOUT_OPTIONS,
} from "@/modules/offstreet-parking/off-street-parking.const";
import { ParkingLayout } from "@/types/gql/graphql";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TimeSpanListing } from "components/time-span";
import { addressPosition } from "services/web/mapbox.service";
import clsx from "clsx";
import { useDebounce } from "hooks/lib-ui";
import { useForm } from "react-hook-form";
import { usePlacesQueryV5 } from "services/web/map.queries";
import useToggle from "hooks/use-toggle";
import { zodResolver } from "@hookform/resolvers/zod";

interface ICoordinates {
  latitude: number;
  longitude: number;
}

interface OffstreetParkingPromptProps {
  coordinates?: ICoordinates;
  offStreetParking: IOffStreetParking;
  onSave: (input: IOffStreetParking) => Promise<void>;
  parkingModalState: keyof typeof OffStreetParkingModalState;
  setParkingModalState: React.Dispatch<React.SetStateAction<keyof typeof OffStreetParkingModalState>>;
  onClose: () => void;
  setOffStreetParkingDetail: (value: IOffStreetParking) => void;
}

const offStreetParkingSchema = z.object({
  name: z.string().min(1, "Parking name is required"),
  address: z.string().min(1, "Address is required"),
  externalId: z.string().nullish(),
  totalPlaces: z.number().min(0, "Total places must be a positive number").nullish(),
  pricePerHour: z.number().min(0, "Price per hour must be a positive number").nullish(),
  maxHeight: z.number().min(0, "Max height must be a positive number").nullish(),
  levels: z.number().min(0, "Levels must be a positive number").nullish(),
  layout: z.enum(ParkingLayout).optional().nullable(),
  options: z
    .object({
      evChargers: z.number().min(0, "EV chargers must be a positive number").nullish(),
      disabledPlaces: z.number().min(0, "Disabled places must be a positive number").nullish(),
      motorcyclePlaces: z.number().min(0, "Motorcycle places must be a positive number").nullish(),
      bicyclePlaces: z.number().min(0, "Bicycle places must be a positive number").nullish(),
      toilets: z.boolean().optional(),
      disabledAccess: z.boolean().optional(),
      motorcycleAccess: z.boolean().optional(),
      bicycleAccess: z.boolean().optional(),
    })
    .optional(),
});

type OffStreetParkingFormValues = z.infer<typeof offStreetParkingSchema>;

export const OffStreetParkingModal = ({
  coordinates,
  offStreetParking,
  parkingModalState,
  setParkingModalState,
  onClose,
  onSave,
  setOffStreetParkingDetail,
}: OffstreetParkingPromptProps) => {
  const form = useForm<OffStreetParkingFormValues>({
    resolver: zodResolver(offStreetParkingSchema),
    defaultValues: {
      ...offStreetParking,
      options: {
        ...offStreetParking.options,
        toilets: offStreetParking.options?.toilets ?? false,
        disabledAccess: offStreetParking.options?.disabledAccess ?? false,
        motorcycleAccess: offStreetParking.options?.motorcycleAccess ?? false,
        bicycleAccess: offStreetParking.options?.bicycleAccess ?? false,
      },
    },
    mode: "all",
  });

  const {
    setValue,
    formState: { isSubmitting },
    control,
  } = form;

  const [toggleMoreOptions, setToggleMoreOptions] = useToggle(false);
  const [address, setAddress] = useState<string>(offStreetParking?.address);
  const [otherAddresses, setOtherAddresses] = useState<string[]>(offStreetParking?.otherAdresses || []);
  const [openingHours, setOpeningHours] = useState(offStreetParking.openingHours || []);
  const [selectedIndexOtherAddress, setSelectedIndexOtherAddress] = useState<number | undefined>();

  const currentAddress = useMemo(() => {
    if (typeof selectedIndexOtherAddress === "number") {
      return otherAddresses[selectedIndexOtherAddress];
    }
    return address;
  }, [address, selectedIndexOtherAddress, otherAddresses]);

  const searchString = useDebounce(currentAddress);

  const handleSearch = (value: string) => {
    setAddress(value);
    setValue("address", value);
  };
  const { data, isLoading } = usePlacesQueryV5({
    keyWord: searchString,
    coordinates: coordinates,
  });

  const listPlace = useMemo(() => {
    if (data && data.length > 0) {
      return data.map((item: addressPosition) => {
        return {
          value: `${item.latitude}:${item.longitude}`,
          label: item.name,
        };
      });
    }
    return [];
  }, [data]);

  const handleSelectPlace = (value: string, label: string) => {
    setAddress(label);
    setValue("address", label);
    const addressPoint = value.split(":");
    setOffStreetParkingDetail({
      ...offStreetParking,
      location: { type: "Point", coordinates: [Number(addressPoint[0]), Number(addressPoint[1])] },
    });
  };

  const handleAddNewAddress = () => {
    setOtherAddresses((prev) => [...prev, ""]);
  };

  const handleRemoveOtherAddress = (idx: number) => {
    const addresses = otherAddresses.filter((_, index) => index !== idx);
    setOtherAddresses(addresses);
  };

  const handleClose = () => {
    setOtherAddresses([]);
    onClose();
  };

  const handleMinimizeModal = () => {
    setParkingModalState(OffStreetParkingModalState.MINIMIZE);
  };

  const handleShowModal = () => {
    setParkingModalState(OffStreetParkingModalState.SHOW);
  };

  const onSubmit = async (input: OffStreetParkingFormValues) => {
    const formattedInput = {
      ...offStreetParking,
      ...input,
      maxHeight: Number(input.maxHeight),
      levels: Number(input.levels),
      otherAdresses: otherAddresses.filter((item) => !!item).map((item) => item),
      location: offStreetParking.location,
      openingHours: openingHours,
      options: {
        ...input.options,
        evChargers: Number(input.options?.evChargers),
        disabledPlaces: Number(input?.options?.disabledPlaces),
        motorcyclePlaces: Number(input?.options?.motorcyclePlaces),
        bicyclePlaces: Number(input.options?.bicyclePlaces),
      },
    };
    await onSave({ ...formattedInput, cityId: offStreetParking.cityId });
    setOtherAddresses([]);
  };
  const modalMinimize = parkingModalState === OffStreetParkingModalState.MINIMIZE;

  return (
    <>
      <Dialog open={!modalMinimize}>
        <DialogContent className="h-[calc(100vh-5em)] overflow-auto">
          <DialogHeader>
            <DialogTitle>{`${offStreetParking.id ? "Edit " : "Add "}a off street parking`}</DialogTitle>
            <DialogDescription>Off street Parking information</DialogDescription>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <FormField
                control={control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Parking Name <span className="text-red-500">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name="address"
                render={({ field: { value } }) => (
                  <FormItem>
                    <FormLabel>
                      Parking Address <span className="text-red-500">*</span>
                    </FormLabel>
                    <FormControl>
                      <DropdownAutoCompleteSelect
                        onChangeValue={handleSelectPlace}
                        value={value}
                        displayValue={address}
                        searchString={address}
                        handleSearch={handleSearch}
                        options={listPlace}
                        isLoading={isLoading}
                        onClick={() => setSelectedIndexOtherAddress(undefined)}
                        className="max-w-[459px]"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="flex items-center gap-2">
                <span className="text-base font-semibold">Location:</span>
                {offStreetParking?.location && (
                  <Button className="text-blue-500" onClick={handleMinimizeModal} variant="ghost" type="button">
                    {offStreetParking?.location.coordinates.toString()}
                  </Button>
                )}
                <EditIcon className="w-5 h-5 cursor-pointer fill-black" onClick={handleMinimizeModal} />
              </div>
              <div className="grid gap-2">
                <Label>Other Addresses</Label>
                {otherAddresses.map((item, index) => (
                  <div className="flex" key={`otherAddresses_${index}`}>
                    <div className="flex-1">
                      <DropdownAutoCompleteSelect
                        placeholder={`Address ${index + 1}`}
                        onChangeValue={(value, label) => {
                          const addresses = [...otherAddresses];
                          addresses[index] = label;
                          setOtherAddresses(addresses);
                        }}
                        handleSearch={(value) => {
                          const addresses = [...otherAddresses];
                          addresses[index] = value;
                          setOtherAddresses(addresses);
                        }}
                        options={listPlace}
                        isLoading={isLoading}
                        value={item}
                        displayValue={item}
                        searchString={item}
                        onClick={() => setSelectedIndexOtherAddress(index)}
                        className="max-w-[435px]"
                      />
                    </div>
                    <IconButton className="w-6 h-6">
                      <DeleteIcon className="w-4 h-4" onClick={() => handleRemoveOtherAddress(index)} />
                    </IconButton>
                  </div>
                ))}
                <IconButton className="w-8 h-8">
                  <AddIcon className="h-6 w-6" fill="black" onClick={handleAddNewAddress} />
                </IconButton>
              </div>

              <div className="grid gap-2">
                <Label>Opening hours</Label>
                <TimeSpanListing
                  label="Opening hours"
                  labelClassName="block leading-5 mb-1 text-sm text-base"
                  timeSpans={openingHours}
                  setTimeSpans={setOpeningHours}
                  suffixClassName="w-8 h-8"
                  suffixIcon={<AddIcon fill="black" className="h-6 w-6 cursor-pointer" />}
                  options={{
                    showDesignatedPeriod: true,
                    showDesignatedPeriodExcept: true,
                    showSelectDate: true,
                    showTimeOfDay: true,
                  }}
                />
              </div>

              <FormField
                control={control}
                name="totalPlaces"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Parking capacity</FormLabel>
                    <FormControl>
                      <InputNumber {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name="pricePerHour"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Price per hour</FormLabel>
                    <FormControl>
                      <InputNumber currency="€" {...field} min={0} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name="externalId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>External ID</FormLabel>
                    <FormControl>
                      <Input {...field} value={field.value ?? ""} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name="maxHeight"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Max Height</FormLabel>
                    <FormControl>
                      <InputNumber {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name="levels"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Levels</FormLabel>
                    <FormControl>
                      <InputNumber {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name="layout"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Layout</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value ?? ""}>
                      <FormControl>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select a layout" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {PARKING_LAYOUT_OPTIONS.map(({ value, label }) => (
                          <SelectItem key={value} value={value}>
                            {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {toggleMoreOptions && (
                <div className="transition-all duration-500 overflow-hidden space-y-6">
                  <FormField
                    control={control}
                    name="options.evChargers"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Number of EV Chargers</FormLabel>
                        <FormControl>
                          <InputNumber {...field} min={0} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={control}
                    name="options.disabledPlaces"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Number of disabled spots</FormLabel>
                        <FormControl>
                          <InputNumber {...field} min={0} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={control}
                    name="options.motorcyclePlaces"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Number of places for motorcycles</FormLabel>
                        <FormControl>
                          <InputNumber {...field} min={0} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={control}
                    name="options.bicyclePlaces"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Number of places for Bicycle</FormLabel>
                        <FormControl>
                          <InputNumber {...field} min={0} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="space-y-6">
                    <FormField
                      control={control}
                      name="options.toilets"
                      render={({ field: { value, onChange } }) => (
                        <FormItem>
                          <FormControl>
                            <div className="flex items-center space-x-2">
                              <Checkbox id="has-toilets" checked={value} onCheckedChange={onChange} />
                              <label
                                htmlFor="has-toilets"
                                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                              >
                                Has toilets
                              </label>
                            </div>
                          </FormControl>
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={control}
                      name="options.disabledAccess"
                      render={({ field: { value, onChange } }) => (
                        <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                          <FormControl>
                            <div className="flex items-center space-x-2">
                              <Checkbox id="disabled-persons-access" checked={value} onCheckedChange={onChange} />
                              <label
                                htmlFor="disabled-persons-access"
                                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                              >
                                Disabled persons access
                              </label>
                            </div>
                          </FormControl>
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={control}
                      name="options.motorcycleAccess"
                      render={({ field: { value, onChange } }) => (
                        <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                          <FormControl>
                            <div className="flex items-center space-x-2">
                              <Checkbox id="motorcycle-access" checked={value} onCheckedChange={onChange} />
                              <label
                                htmlFor="motorcycle-access"
                                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                              >
                                Motorcycle Access
                              </label>
                            </div>
                          </FormControl>
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={control}
                      name="options.bicycleAccess"
                      render={({ field: { value, onChange } }) => (
                        <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                          <FormControl>
                            <div className="flex items-center space-x-2">
                              <Checkbox id="bicycle-access" checked={value} onCheckedChange={onChange} />
                              <label
                                htmlFor="bicycle-access"
                                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                              >
                                Bicycle Access
                              </label>
                            </div>
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              )}

              <Button type="button" onClick={setToggleMoreOptions} variant="ghost">
                {toggleMoreOptions ? "Hide" : "More options"}
                <ArrowIcon className={clsx("transform size-3", toggleMoreOptions ? "rotate-90" : "-rotate-90")} />
              </Button>
              <DialogFooter className="sm:justify-between">
                <Button onClick={handleClose} variant="secondary" type="button">
                  Cancel
                </Button>
                <Button disabled={isSubmitting} type="submit">
                  Save
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
      <Button
        className={clsx("absolute bottom-12 right-4 shadow-md animate-bounce", modalMinimize ? "flex" : "hidden")}
        onClick={handleShowModal}
        variant="outline"
      >
        <span>Back to edit off street parking</span>
        <ArrowIcon className="w-3 h-3 rotate-90" />
      </Button>
    </>
  );
};
